import ButtonComponent from 'components/button';
import type * as PI from 'interfaces/project';
import { PHOTO_DETECTED_FOR_LIMITED_COLOR_LOGO_ERROR_CODE } from 'interfaces/project';
import { UIModule } from 'store';
import { componentSlot } from 'store/ui';

interface LimitedColorLogoPhotoErrorDialogOptions {
	onClose?: () => void;
}

let activeClose: (() => void) | undefined;
let activeCloseDialog: (() => void) | undefined;
let activeCloseCallbacks: Array<() => void> = [];
let activeGeneration = 0;

export function isLimitedColorLogoPhotoError(photoModel: PI.PhotoModel): boolean {
	return photoModel._error?.code === PHOTO_DETECTED_FOR_LIMITED_COLOR_LOGO_ERROR_CODE;
}

/**
 * Tear down the active limited-color logo error dialog without running its
 * queued onClose callbacks.
 *
 * The dialog is a module-global singleton: re-entrant
 * {@link openLimitedColorLogoPhotoErrorDialog} calls reuse the existing
 * `activeClose`. If the dialog is destroyed by other means (e.g. a route
 * change or a parent dialog stack teardown) the singleton is never cleared,
 * which leaks `activeClose` and suppresses every future error dialog. Callers
 * that own the dialog's lifecycle should invoke this on teardown to release
 * the singleton. Unlike `close`, this does NOT fire the queued onClose
 * callbacks, because a teardown is not a user-initiated dismissal.
 */
export function resetLimitedColorLogoPhotoErrorDialog(): void {
	if (!activeClose) {
		return;
	}

	const closeDialog = activeCloseDialog;
	activeGeneration += 1;
	activeClose = undefined;
	activeCloseDialog = undefined;
	activeCloseCallbacks = [];
	closeDialog?.();
}

export function openLimitedColorLogoPhotoErrorDialog(options?: LimitedColorLogoPhotoErrorDialogOptions): () => void {
	if (options?.onClose) {
		activeCloseCallbacks.push(options.onClose);
	}

	if (activeClose) {
		return activeClose;
	}

	let closed = false;
	const generation = activeGeneration;
	const close = (): void => {
		if (closed || generation !== activeGeneration) {
			return;
		}

		closed = true;
		const closeDialog = activeCloseDialog;
		const closeCallbacks = activeCloseCallbacks.slice(0);
		activeGeneration += 1;
		activeClose = undefined;
		activeCloseDialog = undefined;
		activeCloseCallbacks = [];
		closeDialog?.();
		closeCallbacks.forEach((callback) => callback());
	};

	activeClose = close;
	activeCloseDialog = UIModule.openDialog({
		header: componentSlot({
			slot: window.App.router.$t('dialogs.photoNotSupportedLimitedColors.title'),
		}),
		body: componentSlot({
			slot: window.App.router.$t('dialogs.photoNotSupportedLimitedColors.message'),
		}),
		footer: componentSlot({
			tag: ButtonComponent,
			props: {
				class: 'grow',
				onClick: close,
			},
			slot: window.App.router.$t('dialogs.photoNotSupportedLimitedColors.buttons.accept'),
		}),
		props: {
			onClose: close,
			'onClick:outside': close,
		},
	});

	return close;
}
